import type {
  CitationsFormState,
  GrepFormState,
  MapFormState,
  PlaygroundOp,
  SearchContext,
  SearchFormState,
} from "../types";

export interface BuiltCommand {
  /** `command` field for POST /api/cli/execute. */
  command: string;
  /** `raw` field (everything after the command name). */
  raw: string;
  /**
   * Command as shown in the playground console — WITHOUT the `paperclip`
   * binary name, since the console is implicitly a paperclip shell (users
   * read/run `search …`, `grep …`, `map …`). The copy-to-CLI affordance
   * re-adds the `paperclip` prefix so the copied line runs in a real shell.
   */
  display: string;
}

/** Quote a user-provided value as a single shell-style argument. */
export const quote = (value: string): string =>
  `"${value.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;

const PAPER_FILTER_SOURCES = new Set([
  "papers",
  "pmc",
  "biorxiv",
  "medrxiv",
  "arxiv",
  "abstracts",
]);

/** Dedicated FDA/trials/patents/proteins backends do not share paper flags. */
export const supportsPaperSearchFilters = (source: string): boolean =>
  PAPER_FILTER_SOURCES.has(source);

export const buildSearchCommand = (form: SearchFormState): BuiltCommand => {
  // Trials search is one source; the country filter narrows it to a registry
  // route the backend already understands (`-s trials/us`, `trials/eu`, …).
  const source =
    form.source === "trials" && form.trialsCountry
      ? `trials/${form.trialsCountry}`
      : form.source;
  // `--json` is an internal flag the playground uses to get structured results
  // back from the API. Keep it in `raw` (the request) but omit it from the
  // user-facing `display`, so the copyable CLI line isn't polluted with it.
  const parts = ["-n", String(form.limit), "-s", source];
  const supportsPaperFilters = supportsPaperSearchFilters(form.source);

  // Flags default to off/empty; only non-defaults are emitted, so the
  // preview stays an honest, minimal representation of the form.
  if (supportsPaperFilters && form.exact) parts.push("-e");
  if (supportsPaperFilters && form.titleOnly) parts.push("-t");
  if (supportsPaperFilters && form.boolMode) parts.push("--bool");
  if (supportsPaperFilters && form.since.trim())
    parts.push("--since", form.since.trim());
  else if (
    supportsPaperFilters &&
    !form.year.trim() &&
    !form.yearMin.trim() &&
    !form.yearMax.trim() &&
    !form.excludeYear.trim()
  )
    parts.push("--all");
  if (supportsPaperFilters && form.year.trim())
    parts.push("--year", form.year.trim());
  if (supportsPaperFilters && form.yearMin.trim())
    parts.push("--year-min", form.yearMin.trim());
  if (supportsPaperFilters && form.yearMax.trim())
    parts.push("--year-max", form.yearMax.trim());
  if (supportsPaperFilters && form.sort === "date") parts.push("--sort", "date");
  if (supportsPaperFilters && form.hasFullText) parts.push("--has-full-text");
  if (supportsPaperFilters && form.journal.trim())
    parts.push("--journal", quote(form.journal.trim()));
  if (supportsPaperFilters && form.hasSection.trim())
    parts.push("--has-section", quote(form.hasSection.trim()));
  if (supportsPaperFilters && form.hasBlockType.trim())
    parts.push("--has-block-type", quote(form.hasBlockType.trim()));
  if (supportsPaperFilters && form.excludeJournal.trim())
    parts.push("--exclude-journal", quote(form.excludeJournal.trim()));
  if (supportsPaperFilters && form.excludeYear.trim())
    parts.push("--exclude-year", form.excludeYear.trim());

  parts.push(quote(form.query.trim()));
  const argStr = parts.join(" ");
  return {
    command: "search",
    raw: `--json ${argStr}`,
    display: `search ${argStr}`,
  };
};

export const buildGrepCommand = (
  form: GrepFormState,
  context: SearchContext | null,
): BuiltCommand => {
  const parts: string[] = [];
  // Result-set grep runs as SQL where matching is always case-insensitive
  // and -c / -C are not applied — omit them so the preview stays honest.
  const scopedToResults = form.scope === "results";
  // `--source` narrows the corpus-wide full-text grep to one paper corpus. It
  // only applies to corpus scope (single-paper/result-set grep target a fixed
  // set), and an empty value means all corpora.
  if (form.scope === "corpus" && form.source)
    parts.push("--source", form.source);
  if (form.ignoreCase && !scopedToResults) parts.push("-i");
  if (form.wholeWords) parts.push("-w");
  if (form.fixedStrings) parts.push("-F");
  if (form.countOnly && !scopedToResults) parts.push("-c");
  if (form.boolMode) parts.push("--bool");
  if (form.maxMatches.trim()) parts.push("-m", form.maxMatches.trim());
  if (form.context.trim() && !scopedToResults)
    parts.push("-C", form.context.trim());
  if (form.section.trim()) parts.push("--section", quote(form.section.trim()));
  if (form.blockType.trim())
    parts.push("--block-type", quote(form.blockType.trim()));

  parts.push(quote(form.pattern.trim()));
  parts.push(
    form.scope === "results"
      ? `--from ${context?.resultsId ?? "<results_id>"}`
      : form.scope === "paper"
        ? `/papers/${form.paperId.trim()}/content.lines`
        : "/papers/",
  );
  const raw = parts.join(" ");
  return { command: "grep", raw, display: `grep ${raw}` };
};

export const buildMapCommand = (
  form: MapFormState,
  context: SearchContext | null,
): BuiltCommand => {
  const resultsId = context?.resultsId ?? "<results_id>";
  const parts = [`--from ${resultsId}`];
  if (form.limit.trim()) parts.push("-n", form.limit.trim());
  if (form.offset.trim()) parts.push("--offset", form.offset.trim());
  if (form.outputSchema.trim())
    parts.push("--output-schema", quote(form.outputSchema.trim()));
  parts.push(quote(form.question.trim()));
  const raw = parts.join(" ");
  return { command: "map", raw, display: `map ${raw}` };
};

export const buildCitationsCommand = (
  form: CitationsFormState,
): BuiltCommand => {
  // The graph view is served by /api/citegraph/subgraph, but the same
  // neighborhood is scriptable through the CLI — show that equivalent.
  const raw = `${form.paperId.trim()} -n ${form.perSide} --json`;
  return {
    command: "citations",
    raw,
    display: `citations ${form.paperId.trim()} -n ${form.perSide}`,
  };
};

export const buildCommand = (
  op: PlaygroundOp,
  search: SearchFormState,
  grep: GrepFormState,
  map: MapFormState,
  context: SearchContext | null,
  citations: CitationsFormState,
): BuiltCommand => {
  if (op === "search") return buildSearchCommand(search);
  if (op === "grep") return buildGrepCommand(grep, context);
  if (op === "map") return buildMapCommand(map, context);
  if (op === "citations") return buildCitationsCommand(citations);
  // Console runs free-form input through its own panel, not this builder.
  return { command: "", raw: "", display: "" };
};

/** True when the active operation's form has everything needed to run. */
export const isRunnable = (
  op: PlaygroundOp,
  search: SearchFormState,
  grep: GrepFormState,
  map: MapFormState,
  context: SearchContext | null,
  citations: CitationsFormState,
): boolean => {
  if (op === "search") {
    return search.query.trim().length > 0;
  }
  if (op === "grep") {
    if (grep.pattern.trim().length === 0) return false;
    if (grep.scope === "paper") return grep.paperId.trim().length > 0;
    if (grep.scope === "results") return context !== null;
    return true;
  }
  if (op === "map") return context !== null && map.question.trim().length > 0;
  if (op === "citations") return citations.paperId.trim().length > 0;
  return false;
};
